import { useEffect, useRef, useState } from 'react';
import { Contest, Team } from '../../models/types';
import { apiService } from '../../services/api.service';
import { groupService } from '../../services/group.service';
import i18n from '../../i18n/i18n-setup';

// Loads the contest, its teams, the viewer's group role and the group name; refetches when refreshKey changes.
export const useContestDetailData = (id: string | undefined, refreshKey: number) => {
  const [contest, setContest] = useState<Contest | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [userGroupRole, setUserGroupRole] = useState<'admin' | 'member' | null>(null);
  const [groupName, setGroupName] = useState<string | null>(null);
  // Only the first load of a contest shows the page spinner; refreshes swap data in place so open tabs keep their state.
  const loadedId = useRef<string | undefined>(undefined);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const userProfile = await apiService.getUserProfile();
        setCurrentUserId(userProfile._id);
      } catch (err) {
        console.error('Error fetching user profile:', err);
      }
    };

    fetchCurrentUser();
  }, []);

  useEffect(() => {
    // Refreshes can overlap; only the latest one may write state, or an older response would overwrite newer data.
    let stale = false;
    const fetchContestData = async () => {
      if (!id) return;

      try {
        if (loadedId.current !== id) setLoading(true);
        const contestData = await apiService.getContest(id);
        if (stale) return;

        setContest({
          ...contestData,
          startAt: new Date(contestData.startAt),
          endAt: new Date(contestData.endAt),
          createdAt: new Date(contestData.createdAt),
          updatedAt: new Date(contestData.updatedAt),
        });

        try {
          const roleData = await apiService.getUserGroupRoleForContest(id);
          if (stale) return;
          setUserGroupRole(roleData.userRole);
        } catch (err) {
          // Not a member of the group - read-only view.
          console.error('Error fetching user group role:', err);
          setUserGroupRole(null);
        }

        try {
          const groupResponse = await groupService.getGroup(contestData.groupId);
          if (stale) return;
          setGroupName(groupResponse.data?.name ?? null);
        } catch (err) {
          console.error('Error fetching group name:', err);
          setGroupName(null);
        }

        try {
          const teamsData = await apiService.getTeamsForContest(id);
          if (stale) return;
          setTeams(
            teamsData.map(team => ({
              ...team,
              averagePace: Number(team.averagePace) || 0,
              totalDistance: Number(team.totalDistance) || 0,
              totalTracklog: Number(team.totalTracklog) || 0,
              fastestPace: Number(team.fastestPace) || 0,
              maxDistance: Number(team.maxDistance) || 0,
              createdAt: new Date(team.createdAt),
              updatedAt: new Date(team.updatedAt),
            }))
          );
        } catch (teamErr) {
          console.error('Error fetching teams:', teamErr);
          // On a refresh keep the teams already shown rather than blanking an open tab.
          if (!stale && loadedId.current !== id) setTeams([]);
        }

        if (stale) return;
        loadedId.current = id;
        setLoading(false);
      } catch (err) {
        console.error('Error fetching contest data:', err);
        if (stale) return;
        setError(i18n.t('contests:detail.loadError'));
        setLoading(false);
      }
    };

    fetchContestData();
    return () => {
      stale = true;
    };
  }, [id, refreshKey]);

  return { contest, teams, loading, error, currentUserId, userGroupRole, groupName };
};
